// src/pages/CaseDetail/index.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import Sidebar from '../../components/ui/Sidebar';
import TopBar from '../../components/ui/TopBar';
import Breadcrumb from '../../components/ui/Breadcrumb';
import { useSidebar } from '../../contexts/SidebarContext';
import CaseHeader from './components/CaseHeader';
import {
  computeCaseWorkflowProgress,
  normalizeCasePhasesList,
} from '../../lib/caseWorkflowProgress';
import DetailsTab from './components/DetailsTab';
import PhasesTimelineTab from './components/PhasesTimelineTab';
import FilesTab from './components/FilesTab';
import MessagesTab from './components/MessagesTab';
import IncidentsTab from './components/IncidentsTab';
import ProductionLotsTab from './components/ProductionLotsTab';
import CaseInvoicesTab from './components/CaseInvoicesTab';
import InternalNotesTab from './components/InternalNotesTab';
import Icon from '../../components/AppIcon';
import { toast } from 'sonner';
import Button from '../../components/ui/Button';
import NewConceptionVersionModal from '../../components/cases/NewConceptionVersionModal';
import {
  evaluateCaseLotContinuation,
  canStartNewConceptionVersion,
} from '../../lib/caseLotContinuationRules';
import notificationService from '../../services/notificationService';
import {
  canDeleteCaseInSubmissionPhase,
  deleteCaseInSubmissionPhaseRpc,
} from '../../lib/canDeleteCaseInSubmission';
import { pack } from 'd3';
import { getAllCaseFilesList, syncPhase1FileMilestones } from '../../lib/caseMilestoneFileSync';
import { downloadCaseStorageFile } from '../../lib/caseFileSignedUrls';
import { fetchCrmStudioSettings } from '../../services/crmStudioSettingsService';
import {
  canCancelCaseOrder,
  cancelCaseOrder,
  commandCaseFollowUp,
  getCaseCancellationErrorMessage,
} from '../../lib/caseLifecycleActions';
import {
  getCaseAssistantPath,
  getSubmissionPhaseStatusFromPhases,
  mustCompleteAssistantBeforeCaseDetail,
} from '../../lib/dentistSubmissionWizard';
import {
  isOperatorFamilyRole,
  isProductionManagerRole,
  OPERATOR_PRIMARY_ROLE_PRIORITY,
  PRODUCTION_MANAGER_ROLE_TYPES,
} from '../../lib/operatorRoles';
import { useMilestoneTemplateActionsMap } from '../../hooks/useMilestoneTemplateActionsMap';
import { getIncidentWorkflowLabel } from '../../lib/incidentWorkflow';
import { getCasePriorityLabel } from '../../lib/casePriority';
import {
  countUnreadMessagesForViewer,
  dispatchCaseMessagesReadEvent,
  markCaseMessagesAsRead,
  markMessagesReadLocallyForViewer,
} from '../../lib/caseUnreadMessages';

/** Rôles internes pouvant faire avancer le workflow incident (hors dentiste / fournisseur dentaire). */
function userCanTreatCaseIncidents(roleTypes) {
  const list = Array.isArray(roleTypes) ? roleTypes : [];
  if (list.includes('administrator')) return true;
  if (list.some((r) => isProductionManagerRole(r))) return true;
  if (list.some((r) => isOperatorFamilyRole(r))) return true;
  return false;
}

const CASE_DETAIL_ROLE_PRIORITY = [
  'administrator',
  'agent_administratif',
  'dentist_client',
  'dentist_supplier',
  ...PRODUCTION_MANAGER_ROLE_TYPES,
  ...OPERATOR_PRIMARY_ROLE_PRIORITY,
  'commercial',
  'distributor',
  'production_supplier',
];

const CASE_DETAIL_PHASE_SELECT = `
  *,
  phase_template:phase_templates(case_detail_visible_role_types),
  milestones:phase_milestones(
    *,
    milestone_template:milestone_templates(case_detail_visible_role_types),
    validated_by:user_profiles!phase_milestones_validated_by_user_id_fkey(full_name)
  ),
  responsible:user_profiles!case_timeline_phases_responsible_user_id_fkey(full_name)
`;

const normalizeVisibleRoleTypes = (roles) =>
  Array.isArray(roles) ? roles.filter((role) => typeof role === 'string' && role.trim() !== '') : [];

const getCaseDetailStatusLabel = (status) => {
  const labels = {
    draft: 'Brouillon',
    submitted: 'Soumis',
    in_review: 'En révision',
    in_progress: 'En cours',
    completed: 'Terminé',
    cancelled: 'Annulé',
    on_hold: 'En attente',
    pending: 'En attente',
    blocked: 'Bloqué',
  };
  return labels[status] || status;
};

const buildPhaseTemplateVisibilityMap = (phaseTemplates) => {
  const map = {};
  (phaseTemplates || []).forEach((template) => {
    const key = `${template.group ?? ''}_${template.phase_order}`;
    map[key] = normalizeVisibleRoleTypes(template.case_detail_visible_role_types);
  });
  return map;
};

const formatCaseTimelinePhases = (phasesData, fallbackVisibilityByPhaseKey = {}) =>
  (phasesData || []).map((phase) => {
    const phaseKey = `${phase.group ?? ''}_${phase.phase_order}`;
    const repeatedLotFallbackKey =
      Number.parseInt(String(phase.group ?? ''), 10) > 1 ? `2_${phase.phase_order}` : null;
    const fallbackPhaseVisibleRoles =
      fallbackVisibilityByPhaseKey[phaseKey] ??
      (repeatedLotFallbackKey ? fallbackVisibilityByPhaseKey[repeatedLotFallbackKey] : []);
    const phaseVisibleRoles =
      normalizeVisibleRoleTypes(phase.phase_template?.case_detail_visible_role_types).length > 0
        ? normalizeVisibleRoleTypes(phase.phase_template?.case_detail_visible_role_types)
        : normalizeVisibleRoleTypes(fallbackPhaseVisibleRoles);

    const resolvedMilestones = (phase.milestones || [])
      .sort((a, b) => a.milestone_order - b.milestone_order)
      .map((m) => ({
        id: m.id,
        name: m.milestone_name,
        completion_type: m.completion_type ?? null,
        validatedByName: m.validated_by?.full_name ?? null,
        validatedAt: m.validated_at ?? null,
        validated_by_user_id: m.validated_by_user_id ?? null,
        description: m.description,
        note: m.note ?? null,
        is_rejected: m.is_rejected ?? false,
        rejected_date: m.rejected_date ?? null,
        milestone_order: m.milestone_order,
        milestone_template_id: m.milestone_template_id ?? null,
        updated_at: m.updated_at,
        completed: m.is_completed === true,
        completedDate: m.completed_date
          ? new Date(m.completed_date).toLocaleDateString('fr-FR')
          : null,
        _caseDetailVisibleRoleTypes: normalizeVisibleRoleTypes(
          m.milestone_template?.case_detail_visible_role_types
        ),
      }));

    return {
      id: phase.id,
      phase_order: phase.phase_order,
      total_aligners: phase.total_aligners ?? null,
      status: phase.status,
      _rawMilestones: phase.milestones,
      type: phase.phase_name.toLowerCase().replace(/\s+/g, '_'),
      name: phase.phase_name,
      group: phase.group == null || phase.group === '' ? null : String(phase.group),
      design_review_url: phase.design_review_url || null,
      description: phase.description,
      statusLabel: getCaseDetailStatusLabel(phase.status),
      startDate: phase.start_date ? new Date(phase.start_date).toLocaleDateString('fr-FR') : null,
      endDate: phase.end_date ? new Date(phase.end_date).toLocaleDateString('fr-FR') : null,
      responsible: phase.responsible?.full_name || phase.responsible_team || 'Non assigné',
      responsible_user_id: phase.responsible_user_id ?? null,
      responsible_team: phase.responsible_team ?? null,
      notes: phase.notes,
      progress: phase.progress_percentage ?? 0,
      milestones: resolvedMilestones,
      _caseDetailVisibleRoleTypes: phaseVisibleRoles,
    };
  });




const CaseDetail = () => {
  const navigate = useNavigate();
  const { caseId } = useParams();
  const [searchParams] = useSearchParams();
  const { isCollapsed, toggleSidebar } = useSidebar();
  const [activeTab, setActiveTab] = useState(() => {
    const t = searchParams.get('tab');
    if (t === 'files') return 'files';
    if (t === 'messages') return 'messages';
    if (t === 'billing') return 'productionLots';
    if (t === 'productionLots' || t === 'invoices') return t;
    return 'phases';
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deliveredCase, setDeliveredCase] = useState(null);
  const [showRaffinementModal, setShowRaffinementModal] = useState(false);
  const [hasFollowUpLotInProgress, setHasFollowUpLotInProgress] = useState(false);
  const [parentCaseLink, setParentCaseLink] = useState(null);
  const [childCaseLink, setChildCaseLink] = useState(null);

  const [showCommanderModal, setShowCommanderModal] = useState(false);
  const [isCommanderLoading, setIsCommanderLoading] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [isCancelLoading, setIsCancelLoading] = useState(false);
  const [deleteCaseLoading, setDeleteCaseLoading] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [caseData, setCaseData] = useState(null);
  const [phases, setPhases] = useState({ totalDuration: 0, items: [] });
  const [workflowProgress, setWorkflowProgress] = useState(null);
  const milestoneTemplateActions = useMilestoneTemplateActionsMap(phases);
  const [files, setFiles] = useState([]);
  const [messages, setMessages] = useState([]);
  const [incidents, setIncidents] = useState([]);
  const [internalNotes, setInternalNotes] = useState([]);
  const [crmStudioSettings, setCrmStudioSettings] = useState(null);

const tabs = [
  { id: 'details', label: 'Détails', icon: 'FileText' },
  { id: 'files', label: 'Fichiers', icon: 'FolderOpen' },
  { id: 'phases', label: 'Phases', icon: 'GitBranch' },
  { id: 'messages', label: 'Messages', icon: 'MessageSquare', badge: activeTab === 'messages' ? 0 : countUnreadMessagesForViewer(messages, currentUser?.id) },
  ...(['administrator', 'dentist_supplier'].includes(currentUser?.role) ? [
    { id: 'incidents', label: 'Incidents', icon: 'AlertCircle', badge: incidents.filter(i => i.status !== 'resolved').length },
  ] : []),
  ...(currentUser?.role === 'dentist_client' ? [] : [
    { id: 'productionLots', label: 'Lots de productions', icon: 'Boxes' },
  ]),
  { id: 'invoices', label: 'Facturation', icon: 'Receipt' },
  ...(['administrator', 'dentist_supplier'].includes(currentUser?.role) ? [
    { id: 'notes', label: 'Notes Internes', icon: 'Lock' }
  ] : []),
];

  // ─────────────────────────────────────────────────────────────────────────────
  // COMMANDER LA SUITE — Dynamic Lot Creation
  //
  //  1. Find the highest group number and check for pending groups
  //  2. If a pending group exists (all phases pending) → activate its first phase
  //  3. Otherwise → create a NEW group (N+1) by copying Group 2's template
  //     phases & milestones, then activate the first phase of the new group
  // ─────────────────────────────────────────────────────────────────────────────
  const handleCommanderLaSuite = async () => {
    if (!caseId) return;
    setIsCommanderLoading(true);
    try {
      const result = await commandCaseFollowUp(supabase, caseId);
      if (!result?.ok) throw new Error(result?.error || 'follow_up_failed');

      toast.success(`Suite commandée pour le cas ${deliveredCase?.case_number || caseId}`);
      setShowCommanderModal(false);
      setDeliveredCase(null);

      // Refresh all data
      const refreshedFiles = await fetchFiles(caseId);
      await fetchPhases(caseId, refreshedFiles);
      setCaseData(prev => ({
        ...prev,
        status: 'in_progress',
        statusLabel: 'En cours',
        currentPhase: 'pre_production',
      }));
    } catch (err) {
      console.error('Error commanding suite:', err);
      toast.error('Erreur: ' + err.message);
    } finally {
      setIsCommanderLoading(false);
    }
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // FETCH DELIVERED CASE — Dynamic detection for any group
  //
  //  Finds the highest ACTIVATED group (at least one phase not pending).
  //  • Group 1 → button shows when last phase (shipping) is in_progress
  //  • Group N≥2 → button shows when ALL phases in that group are completed
  // ─────────────────────────────────────────────────────────────────────────────
  const fetchDeliveredCase = async () => {
    if (!caseId) return;

    const { data: allPhases, error: phasesError } = await supabase
      .from('case_timeline_phases')
      .select('group, phase_order, status')
      .eq('case_id', caseId)
      .order('phase_order', { ascending: false });

    if (phasesError || !allPhases?.length) {
      setDeliveredCase(null);
      setHasFollowUpLotInProgress(false);
      return;
    }

    const lotState = evaluateCaseLotContinuation(allPhases);
    setHasFollowUpLotInProgress(lotState.hasFollowUpLotInProgress);

    if (lotState.canCommanderSuite) {
      const { data: caseInfo } = await supabase
        .from('cases')
        .select('id, case_number, status')
        .eq('id', caseId)
        .single();

      if (caseInfo && caseInfo.status !== 'archived') setDeliveredCase(caseInfo);
      else setDeliveredCase(null);
    } else {
      setDeliveredCase(null);
    }
  };

  const fetchCaseVersionLinks = async (caseInfo) => {
    if (!caseInfo?.id) {
      setParentCaseLink(null);
      setChildCaseLink(null);
      return;
    }

    if (caseInfo.parent_case_id) {
      const { data: parentRow } = await supabase
        .from('cases')
        .select('id, case_number, version_number')
        .eq('id', caseInfo.parent_case_id)
        .maybeSingle();
      setParentCaseLink(
        parentRow
          ? {
              id: parentRow.id,
              caseNumber: parentRow.case_number,
              versionNumber: parentRow.version_number ?? 1,
            }
          : null
      );
    } else {
      setParentCaseLink(null);
    }

    const { data: childRows } = await supabase
      .from('cases')
      .select('id, case_number, version_number, status')
      .eq('parent_case_id', caseInfo.id)
      .order('version_number', { ascending: false });

    if (childRows?.length) {
      const preferred =
        childRows.find((c) => c.status !== 'archived') || childRows[0];
      setChildCaseLink({
        id: preferred.id,
        caseNumber: preferred.case_number,
        versionNumber: preferred.version_number ?? 2,
      });
    } else {
      setChildCaseLink(null);
    }
  };

useEffect(() => {
  
  const fetchCurrentUser = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (user) {
      const { data: profile } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', user.id)
        .single();

      if (profile) {
        const { data: roleAssignments } = await supabase
          .from('user_role_assignments')
          .select(`
            role_id,
            is_active,
            role:user_roles(role_type, role_name)
          `)
          .eq('user_id', user.id)
          .eq('is_active', true);

        const roleTypes = (roleAssignments || [])
          .map((ra) => ra.role?.role_type)
          .filter(Boolean);
        const userRole =
          CASE_DETAIL_ROLE_PRIORITY.find((r) => roleTypes.includes(r)) || roleTypes[0] || 'user';

        setCurrentUser({
          id: user.id,
          name: profile.full_name,
          email: profile.email,
          role: userRole,
          roleTypes,
          initials: profile.full_name?.split(' ').map(n => n[0]).join('').toUpperCase() || 'U',
        });
      }
    }
  };
  fetchCurrentUser();
}, []);

  useEffect(() => {
    let cancelled = false;
    fetchCrmStudioSettings()
      .then((settings) => {
        if (!cancelled) setCrmStudioSettings(settings);
      })
      .catch((err) => {
        console.warn('crm studio settings:', err?.message || err);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (currentUser?.role === 'dentist_client' && activeTab === 'productionLots') {
      setActiveTab('phases');
    }
  }, [currentUser?.role, activeTab]);

  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab === 'messages') setActiveTab('messages');
  }, [searchParams]);

  useEffect(() => {
    if (activeTab !== 'messages' || !caseId || !currentUser?.id) return;

    setMessages((prev) => markMessagesReadLocallyForViewer(prev, currentUser.id));

    let cancelled = false;
    (async () => {
      try {
        await markCaseMessagesAsRead(caseId, currentUser.id);
        await notificationService.markCaseNotificationsAsRead(currentUser.id, caseId);
        dispatchCaseMessagesReadEvent(caseId);
        if (!cancelled) await fetchMessages(caseId);
      } catch (err) {
        console.warn('[CaseDetail] mark messages read:', err?.message || err);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [activeTab, caseId, currentUser?.id]);

  const submissionPhaseStatus = getSubmissionPhaseStatusFromPhases(phases?.items ?? []);
  const assistantGateActive = mustCompleteAssistantBeforeCaseDetail({
    role: currentUser?.role,
    roleTypes: currentUser?.roleTypes,
    submissionPhaseStatus,
  });

  useEffect(() => {
    if (isLoading || !caseId || !assistantGateActive) return;
    navigate(getCaseAssistantPath(caseId), { replace: true });
  }, [isLoading, caseId, assistantGateActive, navigate]);

  useEffect(() => {
    if (!caseId) {
      setIsLoading(false);
      setError(null);
      return;
    }

    if (caseId) fetchDeliveredCase();

    const fetchCaseData = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const { data: caseInfo, error: caseError } = await supabase
          .from('cases')
          .select(`
    *,
    patient:patients!cases_patient_id_fkey(*),
    dentist:user_profiles!cases_user_profile_id_fkey(*),
    assigned_staff:user_profiles!cases_assigned_staff_id_fkey(*),
    prosthetist:user_profiles!cases_prosthetist_id_fkey(*),
    package:commercial_packages!cases_package_fkey(id, package_name, package_code)
  `)
          .eq('id', caseId)
          .single();
         

        if (caseError) throw caseError;
            console.log('Données de la case récupérées:', caseInfo); // Debug log
        setCaseData({
          id: caseInfo.id,
          caseNumber: caseInfo.case_number,
          patientName: caseInfo.patient?.name,
          patientId: caseInfo.patient?.patient_code,
          patientDOB: new Date(caseInfo.patient?.date_birth || '').toLocaleDateString('fr-FR'),
          patientContact: caseInfo.patient?.phone,
          patientIddb: caseInfo.patient?.id,
          patientStatus: caseInfo.patient?.status,
packages: caseInfo.package?.package_name || 'Pas encore défini',
package: caseInfo.package?.id || null,
 assignedStaffId: caseInfo.assigned_staff_id,   // ← add this
  prosthetistId:   caseInfo.prosthetist_id,
          status: caseInfo.status,
          statusLabel: getStatusLabel(caseInfo.status),
          urgency: caseInfo.priority,
          urgencyLabel: getPriorityLabel(caseInfo.priority),
          hasIncidents: false,
          dentistName: caseInfo.dentist?.full_name || 'Non assigné',
          dentistId: caseInfo.user_profile_id,
          assignedStaff: caseInfo.assigned_staff?.full_name || 'Non assigné',
          prosthetist: caseInfo.prosthetist?.full_name || 'Non assigné',
          currentPhase: caseInfo.current_phase,
          createdDate: new Date(caseInfo.created_date).toLocaleDateString('fr-FR', {
            day: '2-digit',
            month: '2-digit', 
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
          }),
          startDate: caseInfo.start_date ? new Date(caseInfo.start_date).toLocaleDateString('fr-FR', {
            day: '2-digit',
            month: '2-digit', 
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
          }) : 'Non définie',
          dueDate: caseInfo.due_date ? new Date(caseInfo.due_date).toLocaleDateString('fr-FR', {
            day: '2-digit',
            month: '2-digit', 
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
          }) : 'Non définie',
          lastUpdated: new Date(caseInfo.updated_at).toLocaleString('fr-FR', {
            day: '2-digit',
            month: '2-digit', 
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
          }),
          caseType: caseInfo.case_type,
          caseTypeLabel: getCaseTypeLabel(caseInfo.case_type),
          treatmentType: caseInfo.treatment_type,
     treatmentTypeLabel: getTreatmentTypeLabel(caseInfo.treatment_type),
          archToTreat: caseInfo.arch_to_treat,
          priority: caseInfo.priority,
          priorityLabel: getPriorityLabel(caseInfo.priority),
          notes: caseInfo.clinical_notes || 'Aucune note clinique',
          archToTreatLabel: getArchToTreatLabel(caseInfo.arch_to_treat),
          questions_notes: caseInfo.questions_notes,
          questionsNotes: caseInfo.questions_notes,
          sets: caseInfo.sets,
          arches: caseInfo.arches,
          versionNumber: caseInfo.version_number ?? 1,
          parentCaseId: caseInfo.parent_case_id ?? null,
        });

        await fetchCaseVersionLinks(caseInfo);

        await fetchTreatmentSpecs(caseId);

        // Files MUST load before phases so milestone checks have real data.
        // fetchFiles returns the array directly to avoid stale state closures.
        const loadedFiles = await fetchFiles(caseId, { caseStatus: caseInfo.status });
        await fetchPhases(caseId, loadedFiles, {
          currentPhase: caseInfo.current_phase,
          caseStatus: caseInfo.status,
        });

        await fetchMessages(caseId);
        await fetchIncidents(caseId);
        await fetchInternalNotes(caseId);

      } catch (err) {
        console.error('Error fetching case data:', err);
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCaseData();
  }, [caseId]);

  // ─────────────────────────────────────────────────────────────────────────────
  // MILESTONE COMPLETION — pure DB flag (is_completed)
  //
  //  All milestones read directly from DB is_completed flag.
  //  File uploads/deletes update the DB via updateFileMilestones() first,
  //  so the DB is always the single source of truth for every milestone.
  //
  //  • "Scans du patient téléchargés"  → set by updateFileMilestones on upload/delete
  //  • "Photos cliniques soumises"     → set by updateFileMilestones on upload/delete
  //  • "Plan de traitement approuvé"   → only ever set manually / by syncPhase1Status
  // ─────────────────────────────────────────────────────────────────────────────
  const resolveMilestoneCompleted = (milestone) => {
    return milestone.is_completed === true;
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // BIDIRECTIONAL PHASE 1 SYNC
  //
  //  ✅ ALL milestones done  → jalons prêts (phase toujours in_progress jusqu’à « Envoyer Conception »)
  //  ❌ ANY milestone undone → case repasse in_progress si besoin
  //                            (reverts automatically when scan/photo deleted)
  //
  //  Guards prevent unnecessary DB writes when status is already correct.
  // ─────────────────────────────────────────────────────────────────────────────
const syncPhase1Status = async (phase, resolvedMilestones) => {
  if (caseData?.status === 'cancelled') return false;

  // FIX: Guard now checks BOTH group and phase_order so group-2 phases
  //      with phase_order=1 are never accidentally advanced/reverted.
  const isGroup1Phase1 =
    String(phase.group ?? '') === '1' && phase.phase_order === 1;
  if (!isGroup1Phase1) return false;                         // ← CHANGED

  const allDone = resolvedMilestones.every(m => m.completed === true);
  const now     = new Date().toISOString();

  console.log('[syncPhase1Status]', {
    phaseId:    phase.id,
    phaseStatus: phase.status,
    allDone,
    milestones: resolvedMilestones.map(m => ({ name: m.name, completed: m.completed })),
  });

  // ── ADVANCE: all milestones complete → submitted ──────────────────────────
  if (allDone) {
    if (phase.status === 'submitted') return false;

    const { error: phaseError } = await supabase
      .from('case_timeline_phases')
      .update({ end_date: now, progress_percentage: 100, updated_at: now })
      .eq('id', phase.id);

    if (phaseError) {
      console.error('Error advancing phase 1 to submitted:', phaseError);
      return false;
    }

    const milestoneIds = (phase.milestones || []).map(m => m.id);
    if (milestoneIds.length > 0) {
      await supabase
        .from('phase_milestones')
        .update({ is_completed: true, completed_date: now, updated_at: now })
        .in('id', milestoneIds);
    }

    return true;
  }

  // ── REVERT: jalons fichiers incomplets → retirer marqueurs « prêt » ───────
  if (phase.status === 'submitted') return false;

  const hadAutoReady = Boolean(phase.end_date) || caseData?.status === 'submitted';
  if (!hadAutoReady) return false;

  const { error: phaseError } = await supabase
    .from('case_timeline_phases')
    .update({ end_date: null, progress_percentage: 0, updated_at: now })
    .eq('id', phase.id);

  if (phaseError) {
    console.error('Error reverting phase 1 to in_progress:', phaseError);
    return false;
  }

  if (caseData?.status === 'submitted') {
    const { error: caseError } = await supabase
      .from('cases')
      .update({ status: 'in_progress', updated_at: now })
      .eq('id', caseId);

    if (caseError) {
      console.error('Error reverting case to in_progress:', caseError);
      return false;
    }

    setCaseData(prev => ({ ...prev, status: 'in_progress', statusLabel: 'En cours' }));
  }

  return true;
};


  // ─────────────────────────────────────────────────────────────────────────────
  // fetchFiles — returns the built array so fetchPhases gets it immediately
  // without relying on async setState (which would cause a stale-closure bug)
  // ─────────────────────────────────────────────────────────────────────────────
  const fetchFiles = async (caseId, { caseStatus = caseData?.status } = {}) => {
    const allFiles = await getAllCaseFilesList(caseId);
    setFiles(allFiles);
    if (caseStatus !== 'cancelled') {
      await syncPhase1FileMilestones(caseId, allFiles);
    }
    return allFiles;
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // fetchPhases — latestFiles is passed explicitly to avoid stale closures
  // ─────────────────────────────────────────────────────────────────────────────
  const fetchPhases = async (
    caseId,
    latestFiles = [],
    { currentPhase: currentPhaseOpt, caseStatus = caseData?.status } = {}
  ) => {
    const { data: phasesData, error } = await supabase
      .from('case_timeline_phases')
      .select(CASE_DETAIL_PHASE_SELECT)
      .eq('case_id', caseId)
      .order('phase_order', { ascending: true });

    if (error) {
      console.error('Error fetching phases:', error);
      return;
    }

    const missingPhaseTemplateId = (phasesData || []).some((phase) => !phase.phase_template_id);
    let fallbackVisibilityByPhaseKey = {};
    if (missingPhaseTemplateId) {
      const { data: phaseTemplates, error: templateError } = await supabase
        .from('phase_templates')
        .select('group, phase_order, case_detail_visible_role_types');
      if (templateError) {
        console.error('Error fetching phase visibility fallback:', templateError);
      } else {
        fallbackVisibilityByPhaseKey = buildPhaseTemplateVisibilityMap(phaseTemplates);
      }
    }

    const formattedPhases = formatCaseTimelinePhases(phasesData, fallbackVisibilityByPhaseKey);

    setPhases({
      totalDuration: 18,
      items: formattedPhases
    });

    setWorkflowProgress(
      computeCaseWorkflowProgress(
        normalizeCasePhasesList(phasesData),
        currentPhaseOpt ?? caseData?.currentPhase ?? null
      )
    );

    // ── Bidirectional sync: advance to submitted OR revert to in_progress ──
    // Runs on every fetchPhases call so file deletions trigger instant revert
  const phase1Raw      = phasesData.find(
  p => String(p.group ?? '') === '1' && p.phase_order === 1   // ← CHANGED
);
const phase1Resolved = formattedPhases.find(
  p => String(p.group ?? '') === '1' && p.phase_order === 1   // ← CHANGED
);

    if (caseStatus !== 'cancelled' && phase1Raw && phase1Resolved) {
      const changed = await syncPhase1Status(phase1Raw, phase1Resolved.milestones);
      if (changed) {
        // A DB write happened — re-fetch phases to show updated status
        await fetchPhasesAfterAdvance(caseId, latestFiles);
      }
    }

    // Re-evaluate whether "Commander la suite" button should appear
    await fetchDeliveredCase();
  };

  // Lightweight re-fetch used only after auto-advance (avoids infinite loop)
  const fetchPhasesAfterAdvance = async (caseId, latestFiles = [], { currentPhase: currentPhaseOpt } = {}) => {
    const { data: phasesData, error } = await supabase
      .from('case_timeline_phases')
      .select(CASE_DETAIL_PHASE_SELECT)
      .eq('case_id', caseId)
      .order('phase_order', { ascending: true });

    if (error) {
      console.error('Error re-fetching phases after advance:', error);
      return;
    }

    const missingPhaseTemplateId = (phasesData || []).some((phase) => !phase.phase_template_id);
    let fallbackVisibilityByPhaseKey = {};
    if (missingPhaseTemplateId) {
      const { data: phaseTemplates, error: templateError } = await supabase
        .from('phase_templates')
        .select('group, phase_order, case_detail_visible_role_types');
      if (templateError) {
        console.error('Error fetching phase visibility fallback:', templateError);
      } else {
        fallbackVisibilityByPhaseKey = buildPhaseTemplateVisibilityMap(phaseTemplates);
      }
    }

    const formattedPhases = formatCaseTimelinePhases(phasesData, fallbackVisibilityByPhaseKey);

    setPhases({ totalDuration: 18, items: formattedPhases });
    setWorkflowProgress(
      computeCaseWorkflowProgress(
        normalizeCasePhasesList(phasesData),
        currentPhaseOpt ?? caseData?.currentPhase ?? null
      )
    );
  };

  const fetchMessages = async (caseId) => {
    const { data: messagesData, error } = await supabase
      .from('case_messages')
      .select('*, sender:user_profiles!case_messages_sender_id_fkey(full_name, email)')
      .eq('case_id', caseId)
      .order('created_at', { ascending: true });

    if (error) {
      console.error('Error fetching messages:', error);
      return;
    }

    setMessages(messagesData.map(m => ({
      id: m.id,
      senderId: m.sender_id,
      senderName: m.sender?.full_name || 'Utilisateur',
      senderInitials: m.sender?.full_name?.split(' ').map(n => n[0]).join('').toUpperCase() || 'U',
      content: m.message_text,
      timestamp: new Date(m.created_at),
      readBy: m.is_read ? [m.read_by] : [],
      is_read: m.is_read === true,
      is_internal: m.is_internal === true,
    })));
  };

  const fetchIncidents = async (caseId) => {
    const { data: incidentsData, error } = await supabase
      .from('case_incidents')
      .select(`
        *,
        reported_by:user_profiles!case_incidents_reported_by_fkey(full_name),
        resolved_by:user_profiles!case_incidents_resolved_by_fkey(full_name)
      `)
      .eq('case_id', caseId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching incidents:', error);
      return;
    }

    const { data: incidentDocumentsData } = await supabase
      .from('case_documents')
      .select('id, file_name, file_path, case_id, document_type')
      .eq('case_id', caseId)
      .eq('document_type', 'report')
      .ilike('file_name', 'incident-%');

    const attachmentByIncidentId = (incidentDocumentsData || []).reduce((acc, doc) => {
      const match = /^incident-([0-9a-f-]+)-/i.exec(doc.file_name || '');
      if (!match) return acc;
      const incidentId = match[1];
      if (!acc[incidentId]) {
        acc[incidentId] = {
          id: doc.id,
          name: doc.file_name.replace(/^incident-[0-9a-f-]+-/, ''),
          fileName: doc.file_name,
          path: doc.file_path,
        };
      }
      return acc;
    }, {});

    const incidentRows = incidentsData || [];
    let activityByIncidentId = {};
    if (incidentRows.length > 0) {
      const incidentIds = incidentRows.map((i) => i.id);
      const { data: activityData, error: activityError } = await supabase
        .from('case_incident_activity')
        .select(
          `
          id,
          incident_id,
          actor_id,
          from_status,
          to_status,
          message,
          created_at,
          actor:user_profiles!case_incident_activity_actor_id_fkey(full_name)
        `,
        )
        .in('incident_id', incidentIds)
        .order('created_at', { ascending: true });
      if (activityError) {
        console.warn('Error fetching case_incident_activity:', activityError);
      } else {
        activityByIncidentId = (activityData || []).reduce((acc, row) => {
          if (!acc[row.incident_id]) acc[row.incident_id] = [];
          acc[row.incident_id].push({
            id: row.id,
            fromStatus: row.from_status,
            toStatus: row.to_status,
            fromLabel: row.from_status ? getIncidentWorkflowLabel(row.from_status) : '—',
            toLabel: getIncidentWorkflowLabel(row.to_status),
            message: row.message,
            actorName: row.actor?.full_name || '—',
            createdAt: row.created_at,
            dateLabel: new Date(row.created_at).toLocaleString('fr-FR'),
          });
          return acc;
        }, {});
      }
    }

    setIncidents(
      incidentRows.map((i) => ({
        id: i.id,
        title: i.title,
        description: i.description,
        severity: i.severity,
        severityLabel: getSeverityLabel(i.severity),
        status: i.status,
        statusLabel: getIncidentWorkflowLabel(i.status),
        category: i.incident_type,
        categoryLabel: getIncidentTypeLabel(i.incident_type),
        reportedBy: i.reported_by?.full_name || 'Inconnu',
        reportedDate: new Date(i.created_at).toLocaleDateString('fr-FR'),
        resolvedDate: i.resolved_date ? new Date(i.resolved_date).toLocaleDateString('fr-FR') : null,
        resolution: i.resolution_notes,
        attachment: attachmentByIncidentId[i.id] || null,
        activities: activityByIncidentId[i.id] || [],
      })),
    );
  };


  const fetchTreatmentSpecs = async (caseId) => {
    const { data: specsData, error } = await supabase
      .from('case_treatment_specs')
      .select('*')
      .eq('case_id', caseId)
      .single();

    if (error && error.code !== 'PGRST116') {
      console.error('Error fetching treatment specs:', error);
      return;
    }

    if (specsData) {
      setCaseData(prev => ({ ...prev, treatmentSpecs: specsData }));
    }
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // Utility helpers
  // ─────────────────────────────────────────────────────────────────────────────
  const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  const getStatusLabel = (status) => {
    const labels = {
      draft: 'Brouillon',
      submitted: 'Soumis',
      in_review: 'En révision',
      in_progress: 'En cours',
      completed: 'Terminé',
      cancelled: 'Annulé',
      archived: 'Archivée',
      on_hold: 'En attente',
      pending: 'En attente',
      blocked: 'Bloqué'
    };
    return labels[status] || status;
  };

  const getArchToTreatLabel = (arch) => {
    const labels = {
      upper: 'Arcade Supérieure',
      lower: 'Arcade Inférieure',
      both: 'Les Deux Arcades'
    };
    return labels[arch] || 'Non défini';
  };
  const getTreatmentTypeLabel = (type) => {
  const labels = {
    'Full Arch': 'Arcade Complète',
    'Partial': 'Traitement Partiel',
    'Comprehensive': 'Traitement Complet',
    'Retention': 'Contention'
  };
  return labels[type] || type || 'Non défini';
};

  const getPriorityLabel = getCasePriorityLabel;

  const getCaseTypeLabel = (type) => {
    const labels = {
      aligners: 'Aligneurs',
      braces: 'Brackets',
      retainer: 'Contention',
      other: 'Autre'
    };
    return labels[type] || type;
  };

  const getSeverityLabel = (severity) => {
    const labels = {
      low: 'Faible',
      medium: 'Moyenne',
      high: 'Haute',
      critical: 'Critique'
    };
    return labels[severity] || severity;
  };

  const getIncidentTypeLabel = (type) => {
    const labels = {
      delay: 'Retard',
      quality_issue: 'Problème de qualité',
      missing_info: 'Information manquante',
      technical_problem: 'Problème technique',
      other: 'Autre'
    };
    return labels[type] || type;
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // Handlers
  // ─────────────────────────────────────────────────────────────────────────────
  const handleBack = () => navigate('/cases-list');
  const handleEdit = () => console.log('Modifier le cas');
  const handleStatusChange = async () => console.log('Mettre à jour le statut');

  const handleSaveDetails = async (formData) => {
    try {
      const { error: caseError } = await supabase
        .from('cases')
        .update({
          case_type: formData.caseType,
          treatment_type: formData.treatmentType,
           arch_to_treat: formData.archToTreat?.toLowerCase(),
          priority: formData.priority,
          clinical_notes: formData.notes,
          questions_notes: formData.treatmentSpecs?.questionsNotes,
          updated_at: new Date().toISOString()
        })
        .eq('id', caseId);

      if (caseError) throw caseError;

      if (formData.treatmentSpecs) {
        const { error: specsError } = await supabase
          .from('case_treatment_specs')
          .upsert({
            case_id: caseId,
            overject: formData.treatmentSpecs.overjet,
            overbite: formData.treatmentSpecs.overbite,
            midline: formData.treatmentSpecs.midline,
            posterior_crossbite: formData.treatmentSpecs.posteriorCrossbite,
            spacing: formData.treatmentSpecs.spacing,
            sequential_distalization: formData.treatmentSpecs.sequentialDistalization,
            expand: formData.treatmentSpecs.expand,
            procline: formData.treatmentSpecs.procline,
            canine: formData.treatmentSpecs.canine,
            molar: formData.treatmentSpecs.molar,
            ipr_anterior: formData.treatmentSpecs.iprAnterior,
            ipr_posterior_right: formData.treatmentSpecs.iprPosteriorRight,
            ipr_posterior_left: formData.treatmentSpecs.iprPosteriorLeft,
            tooth_mvt_restrictions: formData.treatmentSpecs.toothMovementRestrictions,
            attachment_restrictions: formData.treatmentSpecs.attachmentRestrictions,
            extractions: formData.treatmentSpecs.extractions,
            missing_teeth: formData.treatmentSpecs.missingTeeth,
            updated_at: new Date().toISOString()
          }, { onConflict: 'case_id' });

        if (specsError) throw specsError;
      }

      toast('Détails et spécifications enregistrés avec succès!');
      await fetchTreatmentSpecs(caseId);
      setCaseData((prev) =>
        prev
          ? {
              ...prev,
              archToTreat: formData.archToTreat,
              archToTreatLabel: getArchToTreatLabel(formData.archToTreat),
              treatmentType: formData.treatmentType,
            }
          : prev
      );

    } catch (error) {
      console.error('Error saving details:', error);
      toast('Erreur lors de l\'enregistrement: ' + error.message);
    }
  };

  const handleUploadFiles = () => console.log('Télécharger des fichiers');

  const handleDeleteFile = async (fileId) => {
  if (!confirm('Êtes-vous sûr de vouloir supprimer ce fichier?')) return;

  // Map category → supabase table name
  const tableMap = {
    scans:     'case_scans',
    photos:    'case_photos',
    documents: 'case_documents',
    reports:   'case_documents' // reports are stored in case_documents
  };

  // Find the file in local state to determine which table to update
  const file = files.find(f => f.id === fileId);
  if (!file) {
    toast('Fichier introuvable.');
    return;
  }

  const table = tableMap[file.category];
  if (!table) {
    toast(`Catégorie inconnue: ${file.category}`);
    return;
  }

  try {
    const { error } = await supabase
      .from(table)
      .update({ is_active: false })
      .eq('id', fileId);

    if (error) throw error;

    toast('Fichier supprimé avec succès!');

    // ✅ Notify dentist about file deletion
    if (caseData?.dentistId) {
      await notificationService.createNotification({
        userId: caseData.dentistId,
        type: 'warning',
        title: 'Fichier supprimé',
        message: `Le fichier "${file?.name}" du cas ${caseData?.caseNumber || caseId} a été supprimé`,
        link: `/case-detail/${caseId}`,
        metadata: { 
          caseId, 
          caseNumber: caseData?.caseNumber,
          fileName: file?.name,
          fileCategory: file?.category 
        },
      });
    }

    // Refresh files then re-evaluate milestone states with fresh data
    const refreshedFiles = await fetchFiles(caseId);
    await fetchPhases(caseId, refreshedFiles);
  } catch (error) {
    console.error('Error deleting file:', error);
    toast('Erreur lors de la suppression');
  }
};
const handleDownloadFile = async (fileId) => {
  try {
    // Find the file in local state
    const localFile = files.find(f => f.id === fileId);
    if (!localFile) {
      toast('Fichier introuvable.');
      return;
    }

    // Map category to table names
    const categoryMap = {
      scans: 'case_scans',
      photos: 'case_photos',
      documents: 'case_documents',
      reports: 'case_documents'
    };

    const tableName = categoryMap[localFile.category];
    if (!tableName) {
      toast(`Catégorie de fichier inconnue: ${localFile.category}`);
      return;
    }

    // Fetch file info from Supabase database
    const { data: fileData, error: dbError } = await supabase
      .from(tableName)
      .select('file_path, file_name')
      .eq('id', fileId)
      .single();

    if (dbError) throw dbError;

    await downloadCaseStorageFile({
      filePath: fileData.file_path,
      fileName: fileData.file_name || localFile.name,
    });

  } catch (error) {
    console.error('Erreur lors du téléchargement:', error);
    toast('Erreur lors du téléchargement du fichier: ' + error.message);
  }
};
  const handleSendMessage = async (messageData) => {
    try {
      const { error } = await supabase
        .from('case_messages')
        .insert([{
          case_id: caseId,
          sender_id: currentUser.id,
          message_text: messageData.content,
          message_type: 'comment',
          is_internal: false,
          is_read: false
        }]);
      if (error) throw error;
      await fetchMessages(caseId);
    } catch (error) {
      console.error('Error sending message:', error);
      toast('Erreur lors de l\'envoi du message');
    }
  };

  const handleReportIncident = async (incidentData) => {
    try {
      const types = currentUser?.roleTypes || [];
      const isDentistSide =
        (types.includes('dentist_client') || types.includes('dentist_supplier')) &&
        !types.includes('administrator');
      if (isDentistSide) {
        const { count, error: countError } = await supabase
          .from('case_incidents')
          .select('id', { count: 'exact', head: true })
          .eq('case_id', caseId)
          .neq('status', 'resolved');
        if (countError) throw countError;
        if ((count ?? 0) > 0) {
          toast.error(
            'Un incident est déjà en cours sur ce dossier. Attendez sa clôture avant d’en signaler un autre.',
          );
          return;
        }
      }

      const { error } = await supabase
        .from('case_incidents')
        .insert([{
          case_id: caseId,
          incident_type: incidentData.category,
          severity: incidentData.severity,
          title: incidentData.title,
          description: incidentData.description,
          status: 'open',
          reported_by: currentUser.id
        }]);
      if (error) throw error;
      toast('Incident signalé avec succès!');
      await fetchIncidents(caseId);
    } catch (error) {
      console.error('Error reporting incident:', error);
      toast('Erreur lors du signalement');
    }
  };

  const handleIncidentStatusChange = async (incidentId, nextStatus, message) => {
    if (!currentUser?.id || !caseId) {
      throw new Error('Session ou dossier manquant.');
    }
    if (!userCanTreatCaseIncidents(currentUser?.roleTypes)) {
      toast.error('Vous n’avez pas les droits pour traiter cet incident.');
      throw new Error('Forbidden');
    }
    const body = (message || '').trim();
    if (!body) {
      toast.error('Veuillez saisir un message pour cette étape.');
      throw new Error('Validation');
    }

    const incident = incidents.find((i) => i.id === incidentId);
    if (!incident) throw new Error('Incident introuvable');
    const from = incident.status;
    if (from === 'open' && nextStatus !== 'in_progress') {
      toast.error('Transition non autorisée.');
      throw new Error('Validation');
    }
    if (from === 'in_progress' && nextStatus !== 'resolved') {
      toast.error('Transition non autorisée.');
      throw new Error('Validation');
    }
    if (from === 'resolved') {
      toast.error('Cet incident est déjà fermé.');
      throw new Error('Validation');
    }

    try {
      const { error: actError } = await supabase.from('case_incident_activity').insert([
        {
          incident_id: incidentId,
          actor_id: currentUser.id,
          from_status: from,
          to_status: nextStatus,
          message: body,
        },
      ]);
      if (actError) throw actError;

      const updatePayload = { status: nextStatus };
      if (nextStatus === 'resolved') {
        updatePayload.resolved_date = new Date().toISOString();
        updatePayload.resolved_by = currentUser.id;
        updatePayload.resolution_notes = body;
      }

      const { error: upError } = await supabase
        .from('case_incidents')
        .update(updatePayload)
        .eq('id', incidentId);
      if (upError) throw upError;

      toast.success(nextStatus === 'resolved' ? 'Incident fermé.' : 'Incident passé en cours de traitement.');
      await fetchIncidents(caseId);
    } catch (error) {
      console.error('Error updating incident workflow:', error);
      toast.error(error?.message || 'Erreur lors de la mise à jour de l’incident');
      throw error;
    }
  };

  const handleDownloadIncidentAttachment = async (incident) => {
    if (!incident?.attachment?.path) {
      toast('Aucune piece jointe disponible pour cet incident');
      return;
    }

    try {
      await downloadCaseStorageFile({
        filePath: incident.attachment.path,
        fileName: incident.attachment.fileName || incident.attachment.name,
      });
    } catch (error) {
      console.error('Error downloading incident attachment:', error);
      toast(`Erreur lors du telechargement: ${error.message}`);
    }
  };
const fetchInternalNotes = async (caseId) => {
  try {
    const { data: notesData, error } = await supabase
      .from('cases_notes')
      .select(`
        *,
        author:user_profiles!cases_notes_author_id_fkey(id, full_name, email)
      `)
      .eq('case_id', caseId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching internal notes:', error);
      return;
    }

    setInternalNotes((notesData || []).map(note => ({
      id: note.id,
      content: note.notes,
      authorId: note.author?.id || 'unknown',
      authorName: note.author?.full_name || 'Utilisateur inconnu',
      authorInitials: note.author?.full_name?.split(' ').map(n => n[0]).join('').toUpperCase() || 'U',
      authorRole: 'Membre de l\'équipe', // You can enhance this if you store role info
      timestamp: new Date(note.created_at).toLocaleString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }),
      edited: false, // You can add an updated_at field to track edits
      editedAt: null
    })));
  } catch (err) {
    console.error('Unexpected error fetching notes:', err);
  }
};
 const handleAddNote = async (noteContent) => {
  if (!noteContent?.trim()) return;
  
  try {
    const { data, error } = await supabase
      .from('cases_notes')
      .insert([{
        case_id: caseId,
        notes: noteContent.trim(),
        author_id: currentUser.id
      }])
      .select()
      .single();

    if (error) throw error;

    toast.success('Note ajoutée avec succès!');
    await fetchInternalNotes(caseId);
  } catch (error) {
    console.error('Error adding note:', error);
    toast.error('Erreur lors de l\'ajout de la note: ' + error.message);
  }
};
const handleDeleteNote = async (noteId) => {
  if (!confirm('Êtes-vous sûr de vouloir supprimer cette note?')) return;

  try {
    const { error } = await supabase
      .from('cases_notes')
      .delete()
      .eq('id', noteId);

    if (error) throw error;

    toast.success('Note supprimée avec succès!');
    await fetchInternalNotes(caseId);
  } catch (error) {
    console.error('Error deleting note:', error);
    toast.error('Erreur lors de la suppression de la note: ' + error.message);
  }
};

// Updated renderTabContent function for CaseDetail/index.jsx
// Replace the existing renderTabContent function with this version

  const renderTabContent = () => {
    if (isLoading) {
      return (
        <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>
    );
    }

    if (assistantGateActive) {
      return (
        <div className="rounded-xl border border-warning/40 bg-warning/10 px-6 py-8 text-center space-y-4">
          <Icon name="Send" size={32} className="mx-auto text-primary" />
          <p className="text-sm text-foreground max-w-md mx-auto">
            Cette commande doit être complétée via l&apos;envoi guidé (scans, photos, prescription,
            pack et validation) avant d&apos;accéder à la fiche commande.
          </p>
          <Button
            variant="default"
            iconName="ArrowRight"
            iconPosition="right"
            onClick={() => navigate(getCaseAssistantPath(caseId))}
          >
            Continuer l&apos;envoi guidé
          </Button>
        </div>
      );
    }

    switch (activeTab) {
      case 'details':
      return (
        <DetailsTab
          caseData={caseData}
          userRole={currentUser?.role}
          onSave={handleSaveDetails}
          timelinePhaseItems={phases?.items ?? []}
        />
      );

    case 'phases':
        return (
        <PhasesTimelineTab 
          phases={phases} 
            userRole={currentUser?.role}
          userRoleTypes={currentUser?.roleTypes}
          caseId={caseId}
          currentUserId={currentUser?.id}
          currentUserFullName={currentUser?.name}
               caseData={caseData}
          caseFiles={files}
          crmStudioSettings={crmStudioSettings}
          templateActionsByTemplateId={milestoneTemplateActions}
          workflowProgress={
            currentUser?.role === 'dentist_client' ? workflowProgress : null
          }
          readOnly={isWorkflowReadOnly}
          onPhaseUpdated={async () => {
            const { data: caseInfo } = await supabase
              .from('cases')
              .select(`
                *,
                patient:patients!cases_patient_id_fkey(*),
                dentist:user_profiles!cases_user_profile_id_fkey(*),
                assigned_staff:user_profiles!cases_assigned_staff_id_fkey(*),
                prosthetist:user_profiles!cases_prosthetist_id_fkey(*),
                    package:commercial_packages!cases_package_fkey(id, package_name, package_code)

              `)
              .eq('id', caseId)
              .single();
            
            if (caseInfo) {
              setCaseData(prev => ({
                ...prev,
                status: caseInfo.status,
                statusLabel: getStatusLabel(caseInfo.status),
                currentPhase: caseInfo.current_phase,
                lastUpdated: new Date(caseInfo.updated_at).toLocaleString('fr-FR'),
                    package: caseInfo.package?.id || null,        // ✅ add this
    packages: caseInfo.package?.package_name || prev.packages,  // ✅ add thi
      assignedStaffId: caseInfo.assigned_staff_id,   // ← add this
  prosthetistId:   caseInfo.prosthetist_id,       // ← add this
              }));
            }

            const refreshedFiles = await fetchFiles(caseId);
            await fetchPhases(caseId, refreshedFiles, {
              currentPhase: caseInfo?.current_phase ?? caseData?.currentPhase,
            });

            await fetchDeliveredCase();
          }}
        />
      );

      case 'files':
        return (
          <FilesTab
            files={files}
            onUpload={handleUploadFiles}
            onDelete={handleDeleteFile}
            onDownload={handleDownloadFile}
            // After any upload, refresh files AND re-evaluate phase milestones
            onFilesUploaded={async () => {
              const refreshedFiles = await fetchFiles(caseId);
              await fetchPhases(caseId, refreshedFiles);
            }}
            userRole={currentUser?.role}
            userRoleTypes={currentUser?.roleTypes ?? []}
            caseData={caseData}
            patientId={caseData?.patientIddb}
            currentUserId={currentUser?.id}
            timelinePhaseItems={phases?.items ?? []}
            templateActionsByTemplateId={milestoneTemplateActions}
            onPrescriptionSave={handleSaveDetails}
          />
      );

      case 'messages': {
        const isDentistAuthor = caseData?.dentistId && caseData.dentistId === currentUser?.id;
        const isLabStaff = (currentUser?.roleTypes || [currentUser?.role]).some(
          (r) => r === 'administrator' || isOperatorFamilyRole(r)
        );
        const labRecipientIds = isDentistAuthor
          ? [
              caseData?.prosthetistId,
              caseData?.assignedStaffId,
              ...(phases?.items || [])
                .filter((p) => p?.status === 'in_progress' && p?.responsible_user_id)
                .map((p) => p.responsible_user_id),
            ].filter(Boolean)
          : [];
        return (
          <MessagesTab
            messages={messages}
            onSendMessage={handleSendMessage}
            currentUser={currentUser}
            caseId={caseId}
            caseNumber={caseData?.caseNumber}
            recipientId={isDentistAuthor ? null : caseData?.dentistId}
            additionalRecipientIds={labRecipientIds}
            notifyAdmins={Boolean(isDentistAuthor || isLabStaff)}
          />
        );
      }
      case 'incidents':
        return (
          <IncidentsTab
            incidents={incidents}
            onReportIncident={handleReportIncident}
            onIncidentStatusChange={handleIncidentStatusChange}
            onDownloadAttachment={handleDownloadIncidentAttachment}
            userRole={currentUser?.role}
            roleTypes={currentUser?.roleTypes}
            canTreatIncidents={userCanTreatCaseIncidents(currentUser?.roleTypes)}
          />
      );

      case 'productionLots':
        return (
          <ProductionLotsTab clientUserId={caseData?.dentistId} caseId={caseData?.id} />
        );

      case 'invoices':
        return <CaseInvoicesTab caseId={caseData?.id} />;

      case 'notes':
        return (
          <InternalNotesTab
            notes={internalNotes}
            onAddNote={handleAddNote}
            onDeleteNote={handleDeleteNote}
            currentUser={currentUser}
          userRole={currentUser?.role}
        />
      );

      default:
        return null;
    }
  };

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-destructive">Erreur: {error}</p>
          <button onClick={handleBack} className="mt-4 text-primary">Retour</button>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto" />
          <p className="mt-4 text-muted-foreground">Chargement du conception...</p>
        </div>
      </div>
    );
  }

  // ── Compute next Lot number for the modal text ────────────────────────────
  const computeNextLotNumber = () => {
    const allItems = phases?.items || [];
    const groupNums = [...new Set(allItems.map(p => parseInt(p.group)))]
      .filter(n => !isNaN(n))
      .sort((a, b) => a - b);
    const maxGroup = groupNums[groupNums.length - 1] || 1;

    // Check if there's a pending group that would be activated
    const pendingGroup = groupNums.find(g => {
      if (g <= 1) return false;
      const groupPhases = allItems.filter(p => parseInt(p.group) === g);
      return groupPhases.every(p => p.status === 'pending');
    });

    return pendingGroup || (maxGroup + 1);
  };

  const nextLotNumber = computeNextLotNumber();

  const breadcrumbItems = [
    ...(currentUser?.role === 'dentist_client'
      ? [{ label: 'Espace dentiste', path: '/dentiste', icon: 'LayoutGrid' }]
      : [{ label: 'Tableau de Bord', path: '/dashboard', icon: 'LayoutDashboard' }]),
    { label: 'Conception', path: '/cases-list', icon: 'Briefcase' },
    { label: `Conception #${caseData?.caseNumber}`, path: `/case-detail/${caseId}` }
  ];

  const canDeleteCase = canDeleteCaseInSubmissionPhase({
    userRole: currentUser?.role,
    currentUserId: currentUser?.id,
    caseData,
    timelinePhaseItems: phases?.items ?? [],
  });

  const rpcErrorMessages = {
    not_authenticated: 'Vous devez être connecté.',
    case_not_found: 'Conception introuvable.',
    forbidden: 'Vous n’avez pas le droit de supprimer cette conception.',
    case_status_blocks_delete: 'Cette conception ne peut plus être supprimée (statut).',
    submission_phase_missing: 'Phase de soumission introuvable.',
    phase_not_deletable: 'La suppression n’est possible que pendant la phase « Soumission » (non terminée).',
    wrong_submission_phase_name:
      'La suppression n’est autorisée que pendant la phase « Soumission de la commande » (vérifiez le libellé de la phase dans les modèles).',
    delete_failed: 'La suppression a échoué en base de données.',
  };

  const handleDeleteCase = async () => {
    if (!caseId || !canDeleteCase) return;
    const ok = window.confirm(
      `Supprimer définitivement la conception n°${caseData?.caseNumber ?? caseId} ? Cette action est irréversible.`
    );
    if (!ok) return;

    setDeleteCaseLoading(true);
    try {
      const result = await deleteCaseInSubmissionPhaseRpc(supabase, caseId);
      if (!result.ok) {
        const code = result.error || '';
        const msg =
          rpcErrorMessages[code] ||
          (result.detail ? `${rpcErrorMessages.delete_failed} (${result.detail})` : rpcErrorMessages.delete_failed);
        toast.error(msg);
        return;
      }
      toast.success('Conception supprimée.');
      navigate(currentUser?.role === 'dentist_client' ? '/dentiste' : '/cases-list');
    } catch (e) {
      console.error(e);
      toast.error(e?.message || 'Erreur lors de la suppression.');
    } finally {
      setDeleteCaseLoading(false);
    }
  };

  const isWorkflowReadOnly = caseData?.status === 'cancelled';
  const canCancelOrder = canCancelCaseOrder({
    userRole: currentUser?.role,
    userRoleTypes: currentUser?.roleTypes,
    caseStatus: caseData?.status,
    phases: phases?.items ?? [],
  });

  const refreshCaseLifecycleState = async () => {
    const { data } = await supabase
      .from('cases')
      .select('status, current_phase, updated_at')
      .eq('id', caseId)
      .maybeSingle();
    if (!data) return;
    setCaseData((prev) => ({
      ...prev,
      status: data.status,
      statusLabel: getStatusLabel(data.status),
      currentPhase: data.current_phase,
      lastUpdated: data.updated_at
        ? new Date(data.updated_at).toLocaleString('fr-FR')
        : prev?.lastUpdated,
    }));
  };

  const handleCancelCaseOrder = async () => {
    if (!caseId || !canCancelOrder) return;
    setIsCancelLoading(true);
    try {
      const result = await cancelCaseOrder(supabase, caseId);
      if (!result?.ok) {
        toast.error(getCaseCancellationErrorMessage(result?.error));
        setShowCancelModal(false);
        await refreshCaseLifecycleState();
        return;
      }

      setCaseData((prev) => ({
        ...prev,
        status: 'cancelled',
        statusLabel: 'Annulé',
      }));
      setDeliveredCase(null);
      setShowCancelModal(false);
      toast.success(`Commande ${caseData?.caseNumber || caseId} annulée.`);
    } catch (error) {
      console.error(error);
      toast.error('L’annulation a échoué. Réessayez.');
      await refreshCaseLifecycleState();
    } finally {
      setIsCancelLoading(false);
    }
  };

  const showNewConceptionVersion = !isWorkflowReadOnly && canStartNewConceptionVersion({
    caseStatus: caseData?.status,
    phases: phases?.items ?? [],
  });

  return (
    <div className="page-case-detail min-h-screen bg-background">
      <Sidebar isCollapsed={isCollapsed} onToggle={toggleSidebar} />
      <TopBar sidebarCollapsed={isCollapsed} onSidebarToggle={toggleSidebar} />
      <main className={`main-content ${isCollapsed ? 'sidebar-collapsed' : ''}`}>
        <Breadcrumb items={breadcrumbItems} />

        <div className="p-6">
          <CaseHeader
            caseData={caseData}
            onBack={handleBack}
            files={files}
            onEdit={handleEdit}
            userRole={currentUser?.role}
            userRoleTypes={currentUser?.roleTypes}
            crmStudioSettings={crmStudioSettings}
            phases={phases}
            templateActionsByTemplateId={milestoneTemplateActions}
            deliveredCase={isWorkflowReadOnly ? null : deliveredCase}
            showNewConceptionVersion={showNewConceptionVersion}
            onCommanderSuite={() => setShowCommanderModal(true)}
            onRaffinement={() => setShowRaffinementModal(true)}
            canCancelCase={canCancelOrder}
            onCancelCase={() => setShowCancelModal(true)}
            cancelCaseLoading={isCancelLoading}
            canDeleteCase={canDeleteCase}
            onDeleteCase={handleDeleteCase}
            deleteCaseLoading={deleteCaseLoading}
            parentCaseLink={parentCaseLink}
            childCaseLink={childCaseLink}
            workflowProgress={
              currentUser?.role === 'dentist_client' ? workflowProgress : null
            }
          />

          {isWorkflowReadOnly ? (
            <div
              className="mt-4 flex items-start gap-3 rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3"
              role="status"
            >
              <Icon name="Ban" size={18} className="mt-0.5 shrink-0 text-destructive" />
              <div>
                <p className="text-sm font-semibold text-destructive">Commande annulée</p>
                <p className="text-xs text-muted-foreground">
                  Le workflow de production est verrouillé. Les messages et les notes restent disponibles.
                </p>
              </div>
            </div>
          ) : null}

          <div className="mt-6">
            <div className="case-detail-tabs-row bg-card border-b border-border">
              <div className="flex overflow-x-auto">
                {tabs.map((tab) => (
                <button
                    key={tab.id}
                    type="button"
                    disabled={assistantGateActive}
                    onClick={() => {
                      if (!assistantGateActive) setActiveTab(tab.id);
                    }}
                    className={`relative flex items-center gap-1 px-6 py-4 text-sm font-medium transition-colors whitespace-nowrap ${
                      assistantGateActive
                        ? 'opacity-40 cursor-not-allowed text-muted-foreground'
                        : activeTab === tab.id
                        ? 'case-detail-tab-active text-primary border-b-2 border-primary'
                        : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    <Icon name={tab.icon} size={18} />
                    {tab.label}
                    {tab.badge > 0 && (
  <span className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold bg-primary text-primary-foreground">
    {tab.badge}
  </span>
)}
                  </button>
                ))}
              </div>
            </div>

            <div className="case-detail-tab-panel bg-card border border-border rounded-b-lg p-6">
              {renderTabContent()}
            </div>
          </div>
        </div>
      </main>

      {showRaffinementModal && caseData ? (
        <NewConceptionVersionModal
          isOpen={showRaffinementModal}
          onClose={() => setShowRaffinementModal(false)}
          caseData={{
            id: caseData.id,
            caseNumber: caseData.caseNumber,
            patientName: caseData.patientName,
            versionNumber: caseData.versionNumber ?? 1,
          }}
          ownerProfileId={caseData.dentistId || currentUser?.id}
          hasFollowUpLotInProgress={hasFollowUpLotInProgress}
          onSuccess={(result) => {
            setShowRaffinementModal(false);
            toast.success(`Version v${result.versionNumber} créée (${result.caseNumber || ''})`);
            navigate(getCaseAssistantPath(result.newCaseId));
          }}
        />
      ) : null}

      {showCommanderModal && deliveredCase && (
  <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
    <div className="case-detail-commander-dialog bg-white dark:bg-card border border-border rounded-lg shadow-xl w-full max-w-md">
      <div className="p-6">
        <div className="flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mx-auto mb-4">
          <Icon name="RefreshCw" size={32} className="text-primary" />
        </div>
        <h2 className="text-xl font-bold text-foreground text-center mb-2">Commander la suite</h2>
        <p className="text-muted-foreground text-center mb-2">
          Voulez-vous relancer le suivi du cas{' '}
          <span className="font-semibold text-foreground">{deliveredCase.case_number}</span> ?
        </p>
        <p className="text-xs text-muted-foreground text-center mb-6">
          Le statut passera à <strong>En cours</strong> et le{' '}
          <strong>Lot {nextLotNumber}</strong> sera créé et activé.
        </p>
        <div className="flex gap-3">
          <Button variant="outline" fullWidth onClick={() => setShowCommanderModal(false)} disabled={isCommanderLoading}>
            Annuler
          </Button>
          <Button
            variant="default"
            fullWidth
            iconName={isCommanderLoading ? 'Loader' : 'RefreshCw'}
            iconPosition="left"
            onClick={handleCommanderLaSuite}
            disabled={isCommanderLoading}
          >
            {isCommanderLoading ? 'En cours...' : 'Commander la suite'}
          </Button>
        </div>
      </div>
    </div>
  </div>
)}

      {showCancelModal && canCancelOrder ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          role="presentation"
        >
          <div
            className="w-full max-w-md rounded-lg border border-border bg-card p-6 shadow-xl"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="cancel-case-title"
            aria-describedby="cancel-case-description"
          >
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-destructive/10">
              <Icon name="Ban" size={28} className="text-destructive" />
            </div>
            <h2 id="cancel-case-title" className="mb-2 text-center text-xl font-bold text-foreground">
              Annuler la commande ?
            </h2>
            <p id="cancel-case-description" className="mb-6 text-center text-sm text-muted-foreground">
              Vous allez annuler la commande n°{caseData?.caseNumber || caseId} pour{' '}
              <span className="font-semibold text-foreground">{caseData?.patientName}</span>.
              La production ne pourra plus être démarrée.
            </p>
            <div className="flex flex-col-reverse gap-3 sm:flex-row">
              <Button
                variant="outline"
                fullWidth
                disabled={isCancelLoading}
                onClick={() => setShowCancelModal(false)}
              >
                Conserver la commande
              </Button>
              <Button
                variant="destructive"
                fullWidth
                disabled={isCancelLoading}
                loading={isCancelLoading}
                onClick={handleCancelCaseOrder}
              >
                {isCancelLoading ? 'Annulation…' : 'Annuler la commande'}
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default CaseDetail;